import { create } from "zustand";
import type {
  BootstrapConfig,
  MfaRequiredLoginResponse,
  SessionUser,
  UserSecurityStatus,
} from "@gaole/shared";
import { api, clearAccessToken, refreshAccessToken, setTokens } from "../lib/api";
import { reconnectSocket } from "../lib/socket";

type LoginResult =
  | { ok: true }
  | { ok: false; mfaRequired: MfaRequiredLoginResponse };

interface AuthState {
  user: SessionUser | null;
  config: BootstrapConfig | null;
  userSecurity: UserSecurityStatus | null;
  sessionLocked: boolean;
  loading: boolean;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (challengeToken: string, code: string, method: "email" | "totp") => Promise<void>;
  loginWithPin: (email: string, pin: string) => Promise<void>;
  lockSession: () => void;
  fetchUserSecurity: () => Promise<void>;
  register: (email: string, password: string, displayName: string, acceptPolicies: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  moduleEnabled: (key: string) => boolean;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  config: null,
  userSecurity: null,
  sessionLocked: false,
  loading: true,

  bootstrap: async () => {
    try {
      let config = await api<BootstrapConfig>("/config/bootstrap");
      if (!config.user && localStorage.getItem("gaole_refresh")) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          config = await api<BootstrapConfig>("/config/bootstrap");
        }
      }
      set({ config, user: config.user, loading: false });
      if (config.user) {
        void get().fetchUserSecurity();
      }
    } catch {
      set({ loading: false });
    }
  },

  login: async (email, password) => {
    const data = await api<
      | MfaRequiredLoginResponse
      | { accessToken: string; refreshToken: string; user: SessionUser }
    >("/auth/login", {
      body: { email, password },
    });

    if ("status" in data && data.status === "mfa_required") {
      return { ok: false, mfaRequired: data };
    }

    const tokens = data as { accessToken: string; refreshToken: string; user: SessionUser };
    setTokens(tokens.accessToken, tokens.refreshToken);
    set({ user: tokens.user, sessionLocked: false });
    reconnectSocket();
    await get().bootstrap();
    return { ok: true };
  },

  verifyMfa: async (challengeToken, code, method) => {
    const data = await api<{ accessToken: string; refreshToken: string; user: SessionUser }>(
      "/auth/mfa/verify",
      { body: { challengeToken, code, method } },
    );
    setTokens(data.accessToken, data.refreshToken);
    set({ user: data.user, sessionLocked: false });
    reconnectSocket();
    await get().bootstrap();
  },

  loginWithPin: async (email, pin) => {
    const data = await api<{ accessToken: string; refreshToken: string; user: SessionUser }>(
      "/auth/pin/login",
      { body: { email, pin } },
    );
    setTokens(data.accessToken, data.refreshToken);
    set({ user: data.user, sessionLocked: false });
    reconnectSocket();
    await get().bootstrap();
  },

  lockSession: () => {
    clearAccessToken();
    set({ sessionLocked: true });
    reconnectSocket();
  },

  fetchUserSecurity: async () => {
    try {
      const userSecurity = await api<UserSecurityStatus>("/auth/me/security");
      set({ userSecurity });
    } catch {
      /* not signed in */
    }
  },

  register: async (email, password, displayName, acceptPolicies) => {
    if (!acceptPolicies) {
      throw new Error("You must accept the Terms & Conditions and Privacy Policy");
    }
    const data = await api<{ accessToken: string; refreshToken: string; user: SessionUser }>("/auth/register", {
      body: { email, password, displayName, acceptPolicies: true },
    });
    setTokens(data.accessToken, data.refreshToken);
    set({ user: data.user, sessionLocked: false });
    reconnectSocket();
    await get().bootstrap();
  },

  logout: async () => {
    const refresh = localStorage.getItem("gaole_refresh");
    await api("/auth/logout", { body: { refreshToken: refresh } }).catch(() => {});
    setTokens(null, null);
    set({ user: null, userSecurity: null, sessionLocked: false });
    reconnectSocket();
  },

  refreshUser: async () => {
    try {
      const data = await api<{ user: SessionUser }>("/auth/me");
      set({ user: data.user });
    } catch {
      /* not signed in */
    }
  },

  moduleEnabled: (key) => {
    const config = get().config;
    return config ? (config.modules as Record<string, boolean>)[key] !== false : true;
  },
}));
